import React, { useEffect, useRef } from 'react';
import { Copy } from 'lucide-react';
import './warningMenu.css';

interface WarningMenuProps {
    isOpen: boolean;
    onClose: () => void;
    onResolve: () => void;
    description: string;
    anchorEl: HTMLElement | null;
    // Present for calculated-value alerts (e.g. net barrel/gravity mismatch rules) —
    // renders the reported/calculated/difference readout above the Resolve/Cancel
    // buttons, in addition to (not instead of) resolve support.
    diffData?: {
        reportedValue?: number;
        calculatedValue: number;
    };
}

const WarningMenu: React.FC<WarningMenuProps> = ({
    isOpen,
    onClose,
    onResolve,
    description,
    anchorEl,
    diffData
}) => {
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                (!anchorEl || !anchorEl.contains(event.target as Node))
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, anchorEl]);

    // Calculate position based on anchor element
    useEffect(() => {
        if (isOpen && menuRef.current && anchorEl) {
            const anchorRect = anchorEl.getBoundingClientRect();
            const menuRect = menuRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let top = anchorRect.bottom + 4;
            let left = anchorRect.left;

            if (top + menuRect.height > viewportHeight) {
                top = anchorRect.top - menuRect.height - 4;
            }

            if (left + menuRect.width > viewportWidth) {
                left = viewportWidth - menuRect.width - 8;
            }

            if (left < 8) {
                left = 8;
            }

            menuRef.current.style.top = `${top}px`;
            menuRef.current.style.left = `${left}px`;
        }
    }, [isOpen, anchorEl]);

    const handleCopyToClipboard = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!diffData) return;

        const difference = (diffData.reportedValue || 0) - diffData.calculatedValue;
        const textToCopy = `Reported: ${diffData.reportedValue?.toFixed(2) || 'N/A'}\nCalculated: ${diffData.calculatedValue.toFixed(2)}\nDifference: ${difference > 0 ? `+${difference.toFixed(2)}` : difference.toFixed(2)}`;

        navigator.clipboard.writeText(textToCopy).then(() => {
            console.log('Copied to clipboard');
        }).catch(err => {
            console.error('Failed to copy:', err);
        });
    };

    if (!isOpen) return null;

    const difference = diffData ? (diffData.reportedValue || 0) - diffData.calculatedValue : 0;

    return (
        <div
            ref={menuRef}
            className="warningMenu-mainContainer"
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
        >
            {diffData && (
                <div className="warningMenu-diffSection">
                    <div className="warningMenu-diffRow">
                        <span className="warningMenu-diffLabel">Reported:</span>
                        <span className="warningMenu-diffValue">
                            {Number.isFinite(diffData.reportedValue) ? Number(diffData.reportedValue).toFixed(2) : 'N/A'}
                        </span>
                    </div>
                    <div className="warningMenu-diffRow">
                        <span className="warningMenu-diffLabel">Calculated:</span>
                        <span className="warningMenu-diffValue">
                            {Number(diffData.calculatedValue).toFixed(2)}
                        </span>
                    </div>
                    <div className="warningMenu-diffRow warningMenu-diffRow-difference">
                        <span className="warningMenu-diffLabel">Difference:</span>
                        <span className="warningMenu-diffValue">
                            {difference > 0 ? `+${difference.toFixed(2)}` : difference.toFixed(2)}
                        </span>
                    </div>
                    <button
                        className="warningMenu-copyButton"
                        onClick={handleCopyToClipboard}
                        title="Copy to clipboard"
                    >
                        <Copy size={14} />
                    </button>
                </div>
            )}
            <div className="warningMenu-description">{description}</div>
            <div className="warningMenu-actions">
                <button
                    className="warningMenu-button warningMenu-button-cancel"
                    onClick={onClose}
                >
                    Cancel
                </button>
                <button
                    className="warningMenu-button warningMenu-button-resolve"
                    onClick={onResolve}
                >
                    Resolve
                </button>
            </div>
        </div>
    );
};

export default WarningMenu;
